import React from 'react';
import { useSkipLinks } from '../../hooks/useAccessibility';

interface SkipLink {
  id: string;
  label: string;
}

interface SkipLinksProps {
  links?: SkipLink[];
}

const defaultLinks: SkipLink[] = [
  { id: 'main-content', label: 'Skip to main content' },
  { id: 'navigation', label: 'Skip to navigation' },
  { id: 'search', label: 'Skip to search' },
];

export const SkipLinks: React.FC<SkipLinksProps> = ({ links = defaultLinks }) => {
  const { isVisible } = useSkipLinks();
  
  return (
    <div className="skip-links" role="navigation" aria-label="Skip links">
      {links.map((link) => (
        <a
          key={link.id}
          href={`#${link.id}`}
          className={`skip-link ${isVisible ? 'skip-link--visible' : ''}`}
          onClick={(e) => {
            e.preventDefault();
            const target = document.getElementById(link.id);
            if (target) {
              target.focus();
              target.scrollIntoView({ behavior: 'smooth' });
            }
          }}
        >
          {link.label}
        </a>
      ))}
      
      <style jsx>{`
        .skip-links {
          position: absolute;
          top: 0;
          left: 0;
          z-index: 9999;
        }
        
        .skip-link {
          position: absolute;
          left: -10000px;
          top: auto;
          width: 1px;
          height: 1px;
          overflow: hidden;
          background-color: var(--color-primary);
          color: white;
          padding: 0.5rem 1rem;
          text-decoration: none;
          border-radius: 4px;
          font-weight: 500;
        }
        
        .skip-link:focus,
        .skip-link--visible {
          position: static;
          width: auto;
          height: auto;
          margin: 0.5rem;
          display: inline-block;
        }
        
        .skip-link:hover {
          background-color: var(--color-primary-dark);
        }
        
        @media (prefers-reduced-motion: reduce) {
          .skip-link {
            scroll-behavior: auto !important;
          }
        }
      `}</style>
    </div>
  );
};